// src/components/IntexAssumptions.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Alert,
  Paper,
  Grid as MuiGrid
} from "@mui/material";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import {
  PamGrid,
  formatDateTimeForDisplay,
  GridConfigMenu,
  useGridConfigs,
  isPermissionDenied,
  getPermissionDeniedMessage,
} from "@1823-partners/core";

import api, { fetchIntexAssumptions } from "../api";
import { formatDate } from "../utils/ottiUtils";

const IntexAssumptions = ({ userId }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [assumptionsData, setAssumptionsData] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);
  const [quickFilterText, setQuickFilterText] = useState("");

  const gridRef = useRef(null);

  // Generate column definitions dynamically from data
  const generateColumnDefs = useCallback((data) => {
    if (!data || data.length === 0) return [];

    const firstRow = data[0];
    const columns = Object.keys(firstRow).map(field => {
      const column = {
        field: field,
        headerName: field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        resizable: true,
        sortable: true,
        filter: true,
        editable: false
      };

      // Add specific formatting for date fields
      if (field === 'MODIFIED_DATE') {
        column.valueFormatter = params => {
          if (!params.value) return '';
          return formatDateTimeForDisplay(params.value) || '';
        };
      } else if (field === 'ASOFDT') {
        column.valueFormatter = params => {
          if (!params.value) return '';
          return formatDate(params.value, 'MM/DD/YYYY');
        };
      }

      // Check if the field contains numeric data
      const sampleValue = firstRow[field];
      if (sampleValue !== null && sampleValue !== undefined && !isNaN(sampleValue) && sampleValue !== '') {
        column.type = 'numericColumn';
      }

      return column;
    });

    return columns;
  }, []);

  // Load Intex Assumptions data
  const loadIntexData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetchIntexAssumptions();
      const data = response.data || [];

      // Generate column definitions from the data
      const generatedColumns = generateColumnDefs(data);
      setColumnDefs(generatedColumns);
      setAssumptionsData(data);

    } catch (err) {
      console.error("Error fetching Intex Assumptions data:", err);
      if (isPermissionDenied(err)) {
        setError(getPermissionDeniedMessage(err));
      } else {
        setError("Failed to load Intex Assumptions data. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  }, [generateColumnDefs]);

  // Load data on mount
  useEffect(() => {
    loadIntexData();
  }, [loadIntexData]);

  // Own the view-config hook here (rather than letting GridConfigMenu
  // instantiate it internally) so we can bridge gridConfigs.onGridReady to
  // PamGrid's onGridReady prop — without that bridge a slow grid that mounts
  // AFTER the configs response would miss the default-layout auto-apply.
  const gridConfigs = useGridConfigs(api, {
    app: 'otti',
    gridId: 'intex-assumptions',
    gridApiRef: gridRef,
  });

  // GridConfigMenu is stateful and needs gridRef, so it rides through
  // PamGrid's customToolbarComponents slot.
  const gridConfigComponents = useMemo(() => [
    <GridConfigMenu key="grid-config-menu" controller={gridConfigs} />,
  ], [gridConfigs]);

  // Custom toolbar buttons for refresh
  const customToolbarButtons = [
    {
      label: 'Refresh',
      icon: <RefreshIcon />,
      onClick: loadIntexData,
      disabled: isLoading,
      tooltip: 'Refresh data from server'
    }
  ];

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Intex Assumptions
      </Typography>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Intex Assumptions Grid */}
      <Paper sx={{ p: 2 }}>
        <PamGrid
          ref={gridRef}
          title={`Intex Assumptions (${assumptionsData.length})`}
          rowData={assumptionsData}
          columnDefs={columnDefs}
          showToolbar={true}
          showQuickFilter={true}
          quickFilterText={quickFilterText}
          onQuickFilterChange={setQuickFilterText}
          quickFilterPlaceholder="Search all fields..."
          showExport={true}
          exportLabel="Export"
          exportConfig={{ fileName: 'Intex_Assumptions.xlsx' }}
          showClearFilters={true}
          customToolbarButtons={customToolbarButtons}
          customToolbarComponents={gridConfigComponents}
          onGridReady={gridConfigs.onGridReady}
          // Mirror every column/sort/filter gesture into the local stash so
          // unsaved tweaks survive a page refresh. PamGrid surfaces
          // onFilterChanged at the top level; the column events ride through
          // agGridProps as the stashBindings bundle.
          onFilterChanged={gridConfigs.onGridStateChanged}
          agGridProps={gridConfigs.stashBindings}
          isLoading={isLoading}
          loadingMessage="Loading Intex Assumptions..."
          height="400px"
          rowHeight={32}
          getRowId={(data) => data.CUSIP || data.id}
          defaultColDef={{
            flex: 1,
            minWidth: 120,
            cellStyle: { fontSize: '12px' }
          }}
        />
      </Paper>

      {/* Summary Statistics */}
      <Paper sx={{ mt: 3, p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Summary Statistics
        </Typography>
        <MuiGrid container spacing={2}>
          <MuiGrid size={{ xs: 12, sm: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Total Records
            </Typography>
            <Typography variant="h6">
              {assumptionsData.length}
            </Typography>
          </MuiGrid>
          <MuiGrid size={{ xs: 12, sm: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Total Columns
            </Typography>
            <Typography variant="h6">
              {columnDefs.length}
            </Typography>
          </MuiGrid>
          <MuiGrid size={{ xs: 12, sm: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Last Updated
            </Typography>
            <Typography variant="h6">
              {formatDate(new Date(), 'MM/DD/YYYY')}
            </Typography>
          </MuiGrid>
        </MuiGrid>
      </Paper>
    </Box>
  );
};

export default IntexAssumptions;
